"use client";

import { SaleorAuthProvider, useAuthChange } from "@saleor/auth-sdk/react";
import { invariant } from "ts-invariant";
import { createSaleorAuthClient, type FetchRequestInfo } from "@saleor/auth-sdk";
import { Suspense, useState, type ReactNode } from "react";
import { type Client, Provider as UrqlProvider, createClient } from "urql";
import { withRetry } from "@/lib/fetch-retry";
import posthog from "posthog-js";
import { ACCESS_TOKEN_MAX_AGE, REFRESH_TOKEN_MAX_AGE, encodeCookieName } from "./constants";
import { PostHogIdentifySync } from "./posthog-identify-sync";
import { normalizeSaleorGraphqlUrl } from "@/lib/saleor-api-url";
import { getSaleorAuthFetchAdditionalParams } from "@/lib/auth/saleor-auth-fetch-extra";
import { getSaleorUrqlExchanges } from "@/lib/saleor-urql-exchanges";

const saleorApiUrlRaw = process.env.NEXT_PUBLIC_SALEOR_API_URL;
invariant(saleorApiUrlRaw, "Missing NEXT_PUBLIC_SALEOR_API_URL env variable");
/** Must match JWT `iss` byte-for-byte (see @saleor/auth-sdk runAuthorizedRequest). */
const saleorApiUrl = normalizeSaleorGraphqlUrl(saleorApiUrlRaw);

/**
 * Client-side cookie storage for auth tokens.
 * Shares tokens with server-side via cookies.
 *
 * SECURITY:
 * - Cookies are NOT HttpOnly (SDK needs client-side access for Authorization headers)
 * - SameSite=Lax provides CSRF protection
 * - Secure flag ensures HTTPS-only in production
 * - Short access token (15min) limits exposure window
 *
 * Production requirements:
 * - Strong Content Security Policy (CSP) headers
 * - Audit third-party scripts (XSS vectors)
 */
const createClientCookieStorage = () => {
	const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";

	return {
		getItem: (key: string): string | null => {
			if (typeof document === "undefined") return null;
			const cookieName = encodeCookieName(key);
			const match = document.cookie.match(new RegExp(`(^| )${cookieName}=([^;]+)`));
			return match ? decodeURIComponent(match[2]) : null;
		},
		setItem: (key: string, value: string): void => {
			if (typeof document === "undefined") return;
			const cookieName = encodeCookieName(key);
			const maxAge = key.includes("refresh") ? REFRESH_TOKEN_MAX_AGE : ACCESS_TOKEN_MAX_AGE;
			const securePart = isSecure ? "; Secure" : "";
			document.cookie = `${cookieName}=${encodeURIComponent(
				value,
			)}; path=/; max-age=${maxAge}; SameSite=Lax${securePart}`;
		},
		removeItem: (key: string): void => {
			if (typeof document === "undefined") return;
			const cookieName = encodeCookieName(key);
			document.cookie = `${cookieName}=; path=/; max-age=0; SameSite=Lax`;
		},
	};
};

const clientCookieStorage = createClientCookieStorage();

export const saleorAuthClient = createSaleorAuthClient({
	saleorApiUrl,
	refreshTokenStorage: clientCookieStorage,
	accessTokenStorage: clientCookieStorage,
});

const makeUrqlClient = () => {
	const authFetch = (input: RequestInfo | URL, init?: RequestInit) =>
		saleorAuthClient.fetchWithAuth(input as FetchRequestInfo, init, getSaleorAuthFetchAdditionalParams());

	return createClient({
		url: saleorApiUrl,
		suspense: true,
		requestPolicy: "cache-first",
		preferGetMethod: false,
		fetch: withRetry(authFetch) as typeof fetch,
		exchanges: getSaleorUrqlExchanges(),
	});
};

export function AuthProvider({ children }: { children: ReactNode }) {
	invariant(saleorApiUrl, "Missing NEXT_PUBLIC_SALEOR_API_URL env variable");

	const [urqlClient, setUrqlClient] = useState<Client>(() => makeUrqlClient());
	useAuthChange({
		saleorApiUrl,
		onSignedOut: () => {
			posthog.reset();
			setUrqlClient(makeUrqlClient());
		},
		onSignedIn: () => {
			setUrqlClient(makeUrqlClient());
		},
	});

	return (
		<SaleorAuthProvider client={saleorAuthClient}>
			<UrqlProvider value={urqlClient}>
				<Suspense fallback={null}>
					<PostHogIdentifySync />
				</Suspense>
				{children}
			</UrqlProvider>
		</SaleorAuthProvider>
	);
}
